import type { Session } from './queries';

/**
 * SCH-02. Overlap is decided on instants — `starts_at < other.ends_at AND ends_at > other.starts_at`
 * — never on the day grouping, because the organizer can move a session across midnight or into a
 * different day bucket without changing the fact that two of them collide.
 */
export function overlapsBySession(sessions: Session[]): Map<string, Session[]> {
  const spans = sessions.map((session) => ({
    session,
    start: Date.parse(session.startsAt),
    end: Date.parse(session.endsAt),
  }));

  const found = new Map<string, Session[]>();

  for (const a of spans) {
    // A cancelled session cannot be a conflict: there is nothing left to be in two places for.
    if (a.session.isCancelled) {
      continue;
    }

    const clashes = spans
      .filter(
        (b) =>
          b.session.id !== a.session.id &&
          !b.session.isCancelled &&
          a.start < b.end &&
          a.end > b.start,
      )
      .map((b) => b.session);

    if (clashes.length > 0) {
      found.set(a.session.id, clashes);
    }
  }

  return found;
}

export function overlapLabel(clashes: Session[]): string {
  const names = clashes.map((session) => shortTitle(session.title));

  if (names.length === 1) {
    return `Overlaps with ${names[0]}`;
  }
  if (names.length === 2) {
    return `Overlaps with ${names[0]} and ${names[1]}`;
  }
  return `Overlaps with ${names[0]} and ${names.length - 1} more`;
}

/**
 * Session titles here are "Topic: the long explanatory half", and the warning only needs enough
 * to recognise the other session by.
 */
function shortTitle(title: string): string {
  const head = title.split(':').at(0)?.trim() ?? title;
  return head.length >= 4 ? head : title;
}
